import * as THREE from 'three'
import { SVGLoader, StrokeStyle } from 'three/examples/jsm/loaders/SVGLoader'
import { BufferGeometryUtils } from 'three/examples/jsm/utils/BufferGeometryUtils'

export const buildCircularShape = ({
  radius
}: {
  radius: number
}): THREE.Shape => {
  const shape = new THREE.Shape()
    .moveTo(-radius, -radius)
    .absellipse(-radius, -radius, radius, radius, 0, Math.PI * 2, false, 0)
  return shape
}

type BuildRectangularArg = { [key in 'width' | 'height']: number } & {
  cornerRadius: number | number[]
}

// export const buildRectangularShape = ({
//   width,
//   height,
//   cornerRadius
// }: BuildRectangularArg): THREE.Shape => {
//   console.log(cornerRadius)
//   const w = width / 2
//   const h = height / 2
//   const shape = new THREE.Shape()
//     .moveTo(-w, -h)
//     .lineTo(w, -h)
//     .lineTo(w, h)
//     .lineTo(-w, h)
//   return shape
// }
const eps = 0.00001
export const buildRectangularShape = (props: BuildRectangularArg) => {
  const { width, height, cornerRadius: radius = 0 } = props
  const x = width / -2,
    y = height / -2

  // const { x = width / -2, y = height / -2 } = props
  const shape = new THREE.Shape()
  const defaultRadius =
    typeof radius === 'number'
      ? radius < Math.abs(x) && radius < Math.abs(y)
        ? radius
        : Math.min(Math.abs(x), Math.abs(y))
      : 0
  const cornerRadius = { x: defaultRadius, y: defaultRadius }

  const {
    topLeft = cornerRadius,
    topRight = cornerRadius,
    bottomLeft = cornerRadius,
    bottomRight = cornerRadius
  } =
    typeof radius === 'number'
      ? ({} as any)
      : Object.entries(radius).reduce(
          (p, c) => ({
            ...p,
            [c[0]]: Array.isArray(c[1])
              ? { x: c[1][0], y: c[1][1] }
              : { x: c[1], y: c[1] }
          }),
          {}
        )

  if (!radius || radius > Math.abs(x) || radius > Math.abs(y)) {
    return shape
      .moveTo(x, y)
      .lineTo(x, y + height)
      .lineTo(x + width, y + height)
      .lineTo(x + width, y)
  }

  shape.moveTo(x, y + bottomLeft.y)
  // radius topLeft

  shape.lineTo(x, y + height - topLeft.y)

  if (typeof radius === 'number' && !!radius) {
    shape.absarc(
      x + radius,
      y + height - radius,
      radius,
      Math.PI,
      Math.PI / 2,
      true
    )
  } else {
    shape.quadraticCurveTo(x, y + height, x + topLeft.x, y + height)
  }
  // top right
  shape.lineTo(x + width - topRight.x, y + height)
  if (typeof radius === 'number' && !!radius) {
    shape.absarc(
      x + width - radius,
      y + height - radius,
      radius,
      Math.PI / 2,
      0,
      true
    )
  } else {
    shape.quadraticCurveTo(
      x + width,
      y + height,
      x + width,
      y + height - topRight.y
    )
  }
  // bottom right
  shape.lineTo(x + width, y + bottomRight.y)
  if (typeof radius === 'number' && !!radius) {
    shape.absarc(x + width - radius, y + radius, radius, 0, Math.PI * 1.5, true)
  } else {
    shape.quadraticCurveTo(x + width, y, x + width - bottomRight.x, y)
  }
  // bottom left
  shape.lineTo(x + bottomLeft.x, y)
  if (typeof radius === 'number' && !!radius) {
    shape.absarc(x + radius, y + radius, radius, Math.PI * 1.5, Math.PI, true)
  } else {
    shape.quadraticCurveTo(x, y, x, y + bottomLeft.y)
  }
  return shape
}

export const buildRectanularPath = buildRectangularShape
// ({
//   width,
//   height
// }: { [key in 'width' | 'height']: number }): THREE.Path => {
//   const w = width / 2
//   const h = height / 2
//   const path = new THREE.Shape()
//     .moveTo(-w, -h)
//     .lineTo(w, -h)
//     .lineTo(w, h)
//     .lineTo(-w, h)
//   return path
// }

interface BuildStrokedShapes {
  shapes: THREE.Shape[]
  config?: Partial<StrokeStyle>
}

const defaultConfig = {
  strokeWidth: 1,
  strokeColor: '#000',
  strokeLineJoin: 'miter',
  strokeLineCap: 'butt',
  strokeMiterLimit: 4
}

export const buildStrokedShapes = ({ shapes, config }: BuildStrokedShapes) => {
  const g = new THREE.ShapeBufferGeometry(shapes)
  g.computeBoundingBox()
  const [xMid, yMid] = (['x', 'y'] as const).map(
    (c) =>
      -0.5 *
      ((g.boundingBox as THREE.Box3).max[c] -
        (g.boundingBox as THREE.Box3).min[c])
  )

  const holeShapes = shapes.reduce(
    (prev, shape) => [...prev, ...shape.holes],
    [] as THREE.Path[]
  )

  const geometries = [...shapes, ...holeShapes].map((shape) => {
    var points = shape.getPoints()
    var geometry = SVGLoader.pointsToStroke(
      points as $LibBugAny,
      { ...defaultConfig, ...config },
      200,
      0.001
    )
    geometry.translate(xMid, 0, 0)
    return geometry
  })
  const geometry = BufferGeometryUtils.mergeBufferGeometries(geometries)

  return geometry
  // for (var i = 0; i < shapes.length; i++) {
  //   var shape = shapes[i]
  //   if (shape.holes && shape.holes.length > 0) {
  //     for (var j = 0; j < shape.holes.length; j++) {
  //       var hole = shape.holes[j]
  //       holeShapes.push(hole)
  //     }
  //   }
  // }
}
